import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  CopyToClipboardButton,
  DetailsRow,
  FontIcon,
  IColumn,
  IconButton,
  IDragDropEvents,
  Link,
  mergeStyles,
  mergeStyleSets,
  NoDataTile,
  ScrollablePane,
  ShimmeredDetailsListProps,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { DataTable } from "@components/tables/DataTable";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { TenantDeploymentRingSummary } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useUpdateTenantDeploymentRings } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";
import { useFeedbackContext } from "../../../../../libs/stores/index";

interface TenantDeploymentRingProps extends ShimmeredDetailsListProps {
  onRemoveItem: (
    TenantDeploymentRingSummary: TenantDeploymentRingSummary
  ) => void;
  onAddItem: () => void;
  heading: string;
}

export const TenantDeploymentRing = ({
  onRemoveItem,
  onAddItem,
  heading,
  items,
  ...props
}: TenantDeploymentRingProps) => {
  const navigate = useNavigate();
  const { success, error } = useFeedbackContext();
  const theme = useTheme();
  const {
    mutateAsync: updateTenantDeploymentRing
  } = useUpdateTenantDeploymentRings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const draggedItem = useRef<TenantDeploymentRingSummary | undefined>(
    undefined
  );

  const draggedIndex = useRef<number>(-1);

  const dragEnterClass = mergeStyles({
    // override both the background and the color otherwise the default styles are applied
    background: theme.palette.neutralLight,
    backgroundColor: theme.palette.neutralLight
  });

  const insertBeforeItem = async (item: TenantDeploymentRingSummary) => {
    setIsSubmitting(true);

    try {
      const draggedItems = [draggedItem.current!];
      const insertIndex = items.findIndex(i => i.id === item.id);
      const updatedItems = items.filter(
        itm => draggedItems.map(x => x.id).indexOf(itm.id) === -1
      );

      updatedItems.splice(insertIndex, 0, ...draggedItems);

      const updatePromises = Array.from(updatedItems).map(
        async (updatedItem: TenantDeploymentRingSummary, index) => {
          const originalItem = items.find(
            x => x.id === updatedItem.id
          ) as TenantDeploymentRingSummary;

          await updateTenantDeploymentRing({
            deploymentRingId: updatedItem.deploymentRingId,
            tenantId: updatedItem.tenantId,
            id: updatedItem.id,
            fieldDeviceRoleTypeSid: updatedItem.fieldDeviceRoleTypeSid,
            sortOrder: index + 1,
            etag: originalItem!.etag
          });
        }
      );

      await Promise.all(updatePromises);

      success("The deployment rings have been re-ordered.");
    } catch (e) {
      error(
        `There was an error re-ordering deployment rings: ${e.detail ??
          e.message}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDragDropEvents = (): IDragDropEvents => {
    return {
      canDrop: () => !isSubmitting,
      canDrag: () => !isSubmitting,
      onDragEnter: () => dragEnterClass,
      onDragLeave: () => {},
      onDrop: async (draggedItem?: any) => {
        if (draggedItem) {
          await insertBeforeItem(draggedItem);
        }
      },
      onDragStart: (item?: any, itemIndex?: number) => {
        draggedItem.current = item;
        draggedIndex.current = itemIndex!;
      },
      onDragEnd: () => {
        if (isSubmitting) return;
        draggedItem.current = undefined;
        draggedIndex.current = -1;
      }
    };
  };

  const columns: IColumn[] = useMemo(() => {
    return [
      {
        key: "dragDropIcon",
        name: "",
        minWidth: 40,
        maxWidth: 40,
        onRender: () => <FontIcon iconName="DragObject" />
      },
      {
        key: "sortOrder",
        name: "Order",
        minWidth: 100,
        maxWidth: 100,
        isSorted: true,
        isSortedDescending: true,
        onRender: (
          TenantDeploymentRingSummary: TenantDeploymentRingSummary
        ) => <Text>{TenantDeploymentRingSummary.sortOrder}</Text>
      },
      {
        key: "deploymentRingDisplayName",
        name: "Deployment Ring Name",
        minWidth: 200,
        maxWidth: 300,
        isResizable: true,
        onRender: (
          TenantDeploymentRingSummary: TenantDeploymentRingSummary
        ) => (
          <Link
            onClick={() =>
              navigate(
                `/system/plt/deployment-rings/${TenantDeploymentRingSummary.deploymentRingId}`
              )
            }
          >
            {TenantDeploymentRingSummary.deploymentRingDisplayName}
          </Link>
        )
      },
      {
        key: "deploymentRingDescription",
        name: "Deployment Ring Description",
        minWidth: 400,
        isResizable: true,
        onRender: (
          TenantDeploymentRingSummary: TenantDeploymentRingSummary
        ) => (
          <Text>{TenantDeploymentRingSummary.deploymentRingDescription}</Text>
        )
      },
      {
        key: "deploymentRingId",
        name: "Deployment Ring Id",
        minWidth: 300,
        maxWidth: 300,
        isResizable: true,
        onRender: (
          TenantDeploymentRingSummary: TenantDeploymentRingSummary
        ) => (
          <CopyToClipboardButton
            clipboardContent={TenantDeploymentRingSummary.deploymentRingId}
            text={TenantDeploymentRingSummary.deploymentRingId}
          />
        )
      },
      {
        key: "deploymentRingCode",
        name: "Deployment Ring Code",
        minWidth: 200,
        maxWidth: 200,
        isResizable: true,
        onRender: (
          TenantDeploymentRingSummary: TenantDeploymentRingSummary
        ) => <Text>{TenantDeploymentRingSummary.deploymentRingCode}</Text>
      },
      {
        key: "id",
        name: "",
        minWidth: 32,
        onRender: (
          TenantDeploymentRingSummary: TenantDeploymentRingSummary
        ) => (
          <IconButton
            disabled={!hasWritePermission || isSubmitting}
            title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
            onClick={() => onRemoveItem(TenantDeploymentRingSummary)}
            iconProps={{ iconName: "Cancel" }}
            styles={mergeStyleSets(dangerButtonStyles(theme), {
              root: {
                background: "transparent"
              }
            })}
          />
        ),
        sort: false
      }
    ];
  }, [hasWritePermission, isSubmitting, navigate, onRemoveItem, theme]);

  return (
    <>
      {items.length === 0 ? (
        <NoDataTile
          showBoxShadow={false}
          textProps={{ text: "There are no deployment rings assigned" }}
          linkProps={{ text: "" }}
        />
      ) : (
        <ScrollablePane>
          <DataTable
            stickyHeader
            bordersVariant="full"
            shimmerLines={5}
            columns={columns}
            items={items}
            onShouldVirtualize={() => true}
            dragDropEvents={getDragDropEvents()}
            onRenderRow={row => (row ? <DetailsRow {...row} /> : null)}
            {...props}
          />
        </ScrollablePane>
      )}
    </>
  );
};
